import { ChangeEvent, FC } from "react";
import TranscodePresetsPopupContentProps from "./TranscodePresetsPopupContent.interface";
import TranscodePresetForm from "../TranscodePresetForm";
import { CREATE_FORM_ID, DEFAULT_TABLE_ROW, getTableHeaders, UPDATE_FORM_ID } from "../TranscodePresetsPopup.data";
import { ViewModes } from "../TranscodePresetsPopup.interface";
import { useGetTranscodePresetsQuery } from "../../../features/api/apiSlice";
import { TableRow } from "../../Table/Table.interface";
import Table from "../../Table";
import { useScreenWidth } from "../../../hooks/useScreenWidth";
import DropdownMenu from "../../DropdownMenu";
import RadioButton from "../../RadioButton";
import { Button } from "../../Button";

import styles from "./TranscodePresetsPopupContent.module.css";

const TranscodePresetsPopupContent: FC<TranscodePresetsPopupContentProps> = ({
  transcodePresetToUpdate,
  viewMode,
  selectedTranscodePresetId,
  isShowRadioButtons,
  paginationParams,
  onSubmit,
  onChange,
  onChangeViewMode,
  handleTranscodePresetToUpdate,
  setTranscodePresetToDelete,
  functionalItemRef
}) => {
  const { isMobile } = useScreenWidth();
  const { data: transcodePresets, isSuccess: isTranscodePresets } = useGetTranscodePresetsQuery({ ...paginationParams });

  const renderPresetsList = () => {
    const handleChangeSelectedPreset = (e: ChangeEvent<HTMLInputElement>) => {
      const transcodePresetId = Number(e.target.value);
      const isChecked = e.target.checked;

      if (isChecked) {
        onChange(transcodePresetId);
      }
    };

    const getRowParams = (transcodePresetId: number) => {
      if (!transcodePresets) return;

      const transcodePreset = transcodePresets?.transcode_presets.find(({ id }) => id === transcodePresetId);

      if (!transcodePreset) return;

      const { id, name, fps, width, height, video_bitrate, audio_bitrate } = transcodePreset;
      const mobileRowParams = [
        {
          label: "Название",
          value: name
        },
        {
          label: "FPS",
          value: fps
        },
        {
          label: "Разрешение",
          value: `${width}x${height}`
        },
        {
          label: "Битрейт видео",
          value: video_bitrate
        },
        {
          label: "Битрейт аудио",
          value: audio_bitrate
        }
      ];

      const renderFunctionalItem = () => {
        return (
          <div className={styles["content__functions-container"]}>
            {isShowRadioButtons &&
              <RadioButton
                value={id}
                onChange={handleChangeSelectedPreset}
                checked={selectedTranscodePresetId === id}
              />
            }

            <DropdownMenu
              items={[
                {
                  action: () => handleTranscodePresetToUpdate(transcodePreset),
                  iconName: "gear",
                  label: "Настройки",
                },
                {
                  action: () => setTranscodePresetToDelete(transcodePreset),
                  iconName: "trash-bin",
                  label: "Удалить",
                  className: styles["dropdown__item_dangerous"]
                }
              ]}
            />
          </div>
        );
      };

      const generateRowItems = () => {
        if (isMobile) {
          return [
            {
              value:
                <>
                  {mobileRowParams.map(({ label, value }, index) => (
                    <div key={index} className={styles["item-content"]}>
                      <span className={styles["item-content__label"]}>{label}:</span>
                      <span className={styles["item-content__value"]}>{value}</span>
                    </div>
                  ))}
                </>,
              colSpan: 3
            },
            {
              value: renderFunctionalItem()
            }
          ];
        } else {
          return [
            { value: name },
            { value: fps },
            { value: `${width}x${height}` },
            { value: video_bitrate },
            { value: audio_bitrate },
            {
              value: renderFunctionalItem(),
              containerClassName: styles["item-content__functional-Item-container"]
            }
          ];
        }
      };

      return {
        transcodePresetId: id,
        items: generateRowItems(),
        ref: functionalItemRef
      } as TableRow;
    };

    return (
      <>
        {!transcodePresets?.transcode_presets.length && isTranscodePresets ? (
          <div className={styles["empty-state"]}>
            <div className={styles["empty-state__button-text"]}>У вас еще нет добавленных пресетов</div>
            <Button
              kind={"primary"}
              className={styles["empty-state__button"]}
              onClick={() => onChangeViewMode(ViewModes.CREATE_PRESET)}
            >
              + Добавить пресет
            </Button>
          </div>
        ) : (
          <Table
            headers={getTableHeaders(isMobile)}
            content={transcodePresets?.transcode_presets.map(({ id }) => getRowParams(id) ?? DEFAULT_TABLE_ROW)}
            onRowClick={(id) => onChange(id)}
            animationMode={true}
          />
        )}
      </>
    );
  };

  return (
    <>
      {viewMode === ViewModes.PRESETS_LIST && renderPresetsList()}
      {viewMode === ViewModes.CREATE_PRESET && onSubmit && (
        <TranscodePresetForm
          formId={CREATE_FORM_ID}
          onSubmit={onSubmit}
        />
      )}
      {viewMode === ViewModes.EDIT_PRESET && transcodePresetToUpdate && onSubmit && (
        <TranscodePresetForm
          formId={UPDATE_FORM_ID}
          transcodePreset={transcodePresetToUpdate}
          onSubmit={onSubmit}
        />
      )}
    </>
  );
};

export default TranscodePresetsPopupContent;